import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { submitBlog, getBlogById, editBlogServer } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { BookOpen, AlertCircle, Image as ImageIcon, Bold, Italic, List, Link as LinkIcon } from 'lucide-react';

const WriteBlog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const editId = new URLSearchParams(search).get('edit');

  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  
  const editorRef = useRef(null);

  useEffect(() => {
    if (editId) {
      const loadBlog = async () => {
        const blog = await getBlogById(editId);
        if (blog) {
          setTitle(blog.title || '');
          setExcerpt(blog.excerpt || '');
          setContent(blog.content || '');
          if (blog.image && !blog.image.includes('placeholder')) setImage(blog.image);
          setIsEditMode(true);
          if (editorRef.current) {
            editorRef.current.innerHTML = blog.content || '';
          }
        }
      };
      loadBlog();
    }
  }, [editId]);

  const handleFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      setContent(editorRef.current.innerHTML);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !content || content === '<p><br></p>') {
      setError('Title and content are required.');
      return;
    }

    setIsSubmitting(true);
    const blogData = {
      title,
      excerpt,
      content,
      image: image || 'https://via.placeholder.com/800x400?text=Blog+Image',
      author: user?.name || 'Guest User',
      user_id: user?.id,
      role: user?.role || 'user'
    };

    let res;
    if (isEditMode) {
      blogData.id = editId;
      res = await editBlogServer(blogData);
    } else {
      res = await submitBlog(blogData);
    }
    
    setIsSubmitting(false);

    if (res.success) {
      if (user?.role === 'admin') {
        const blogId = isEditMode ? editId : res.blog?.id;
        navigate(`/blog/${blogId || ''}`);
      } else {
        alert(isEditMode ? 'Your edits have been submitted for Admin approval.' : 'Your blog has been submitted and is waiting for Admin approval.');
        navigate('/admin/blogs');
      }
    } else {
      setError("Operation failed. Ensure database has all required fields.");
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="mb-10 flex items-center space-x-4 border-b border-stone-200 pb-6">
          <div className="bg-saffron-100 p-3 rounded-xl border border-saffron-200">
            <BookOpen className="h-6 w-6 text-saffron-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-stone-900">{isEditMode ? 'Edit Blog Post' : 'Write a Blog Post'}</h1>
            <p className="text-stone-600">Share your spiritual journey, historic knowledge, or local updates.</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 border border-red-200 rounded-xl flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" /> {error}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Title</label>
              <input 
                type="text" 
                maxLength={100}
                required
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Enter an engaging title..."
                className="w-full border border-stone-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-medium text-lg placeholder-stone-400"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Short Excerpt</label>
              <textarea 
                maxLength={200}
                rows={2}
                value={excerpt}
                onChange={e => setExcerpt(e.target.value)}
                placeholder="A brief summary of your post to show on the public feed."
                className="w-full border border-stone-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-saffron-500 text-stone-700 placeholder-stone-400 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1 flex items-center">
                <ImageIcon className="w-4 h-4 mr-1 text-stone-400" /> Cover Image
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <input 
                  type="url"
                  value={image.startsWith('data:') ? '' : image}
                  onChange={e => setImage(e.target.value)}
                  placeholder="Paste URL..."
                  className="w-full flex-1 border border-stone-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-saffron-500 text-stone-700 bg-stone-50"
                />
                <span className="text-stone-400 font-medium hidden sm:inline">OR</span>
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => setImage(reader.result);
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="w-full sm:w-auto max-w-full text-sm text-stone-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-saffron-50 file:text-saffron-700 hover:file:bg-saffron-100 cursor-pointer"
                />
              </div>
              {image && (
                <div className="mt-3 relative h-32 w-48 rounded-lg overflow-hidden border border-stone-300 shadow-sm">
                  <img src={image} alt="Cover Preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold text-stone-700 mb-1">Article Content</label>
              <div className="rounded-xl overflow-hidden border border-stone-300 focus-within:ring-2 focus-within:ring-saffron-500 focus-within:border-transparent bg-white flex flex-col h-96">
                <div className="bg-stone-50 border-b border-stone-200 p-2 flex items-center space-x-2">
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('bold'); }} className="p-2 hover:bg-stone-200 rounded text-stone-700 transition"><Bold className="w-4 h-4" /></button>
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('italic'); }} className="p-2 hover:bg-stone-200 rounded text-stone-700 transition"><Italic className="w-4 h-4" /></button>
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('underline'); }} className="p-2 hover:bg-stone-200 rounded text-stone-700 transition px-2 font-serif font-bold underline">U</button>
                  <div className="w-px h-4 bg-stone-300 mx-2"></div>
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('insertUnorderedList'); }} className="p-2 hover:bg-stone-200 rounded text-stone-700 transition"><List className="w-4 h-4" /></button>
                  <button type="button" onMouseDown={(e) => { 
                    e.preventDefault(); 
                    const url = prompt('Enter link URL:'); 
                    if (url) handleFormat('createLink', url); 
                  }} className="p-2 hover:bg-stone-200 rounded text-stone-700 transition"><LinkIcon className="w-4 h-4" /></button>
                </div>
                <div 
                  ref={editorRef}
                  contentEditable
                  onInput={(e) => setContent(e.currentTarget.innerHTML)} 
                  className="flex-1 w-full p-4 overflow-y-auto resize-none focus:outline-none text-stone-700 leading-relaxed font-sans empty:before:content-[attr(data-placeholder)] empty:before:text-stone-400" 
                  data-placeholder="Start pouring your thoughts out... Click formatting buttons to structure your text!"
                />
              </div>
            </div>

            <div className="pt-4 flex items-center justify-between border-t border-stone-100">
              <p className="text-sm text-stone-500">
                {user?.role === 'admin' 
                  ? "As an Admin, your post will be published immediately." 
                  : "Your post will require approval before going live."}
              </p>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3 bg-saffron-600 text-white font-bold rounded-xl hover:bg-saffron-700 transition"
              >
                {isSubmitting ? (isEditMode ? 'Updating...' : 'Submitting...') : (isEditMode ? 'Update Post' : 'Submit Post')}
              </button>
            </div>

          </form>
        </div>

      </div>
    </div>
  );
};

export default WriteBlog;
